import {castDraft, original} from 'immer';
import {createAction} from 'typesafe-actions';

import {
  PanelBankConfigState,
  type PanelPlacementOverride,
  SectionPanelSorting,
} from '../../../components/PanelBank/types';
import {getDefaultPanelSectionConfig} from '../../../util/panelbankConfigs';
import {
  findNextPanelLoc,
  GRID_COLUMN_COUNT,
  GRID_ITEM_DEFAULT_HEIGHT,
  GRID_ITEM_DEFAULT_WIDTH,
} from '../../../util/panelbankGrid';
import {type StateType as ExpectedPanelsState} from '../../expectedPanels/reducer';
import * as Normalize from '../normalize';
import {lookupPart} from '../normalizerSupport';
import type * as PanelTypes from '../panel/types';
import * as PanelUtils from '../panel/utils';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {
  deleteParts,
  removeHistoryForObject,
  type ViewReducerState,
} from '../reducerSupport';
import {type WholeFromTypeWithRef} from '../types';
import type * as WorkspaceSettingsTypes from '../workspaceSettings/types';
import type * as Types from './types';

export const diffAndInitPanels = createAction(
  '@view/panelBankConfig/diffAndInitPanels',
  action =>
    (
      ref: Types.Ref,
      expectedPanels: ExpectedPanelsState,
      /**
       * Pass workspaceSettings ref instead of actual shouldAutoGeneratePanels value
       * because we don't want to trigger this action whenever the setting is updated.
       * We just want to read the setting value when this action *is* triggered.
       */
      workspaceSettingsRef: WorkspaceSettingsTypes.Ref
    ) =>
      action({ref, expectedPanels, workspaceSettingsRef})
);

export const replacePanelBankConfig = createAction(
  '@view/panelBankConfig/replacePanelBankConfig',
  action =>
    (config: WholeFromTypeWithRef<'panel-bank-config'>, ref: Types.Ref) =>
      action({config, ref})
);

export const putSection = createAction(
  '@view/panelBankConfig/putSection',
  action =>
    (
      ref: Types.Ref,
      sectionRef: PanelBankSectionConfigTypes.Ref,
      sectionNorm: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized,
      prevIndex: number,
      deletePanelsResult: PanelTypes.DeletePanelsResult,
      workspaceSettingsRef?: WorkspaceSettingsTypes.Ref
    ) =>
      action({
        ref,
        sectionRef,
        sectionNorm,
        prevIndex,
        deletePanelsResult,
        workspaceSettingsRef,
      })
);

// This completely overwrites panelBankConfig.sections
export const setAllSections = createAction(
  '@view/panelBankConfig/setAllSections',
  action =>
    (
      ref: Types.Ref,
      newSectionsNormalized: PanelBankSectionConfigTypes.PanelBankSectionConfigNormalized[]
    ) =>
      action({ref, newSectionsNormalized})
);

export const undoMovePanelImmer = createAction(
  '@view/panelBankConfig/undoMovePanelImmer',
  action =>
    (
      ref: Types.Ref,
      panelRef: PanelTypes.Ref,
      fromSectionRef: PanelBankSectionConfigTypes.Ref,
      toSectionRef: PanelBankSectionConfigTypes.Ref,
      toIndex: number,
      isPanelAuto: boolean | undefined, // panel.isAuto from before panel was moved; must revert to this value
      isFromSectionPanelsAuto: boolean | undefined, // fromSection.isPanelsAuto from before panel was moved; must revert to this value
      isToSectionPanelsAuto: boolean | undefined, // toSection.isPanelsAuto from before panel was moved; must revert to this value
      newSectionName?: string // if provided, a new section was created as part of the move; need to delete it
    ) =>
      action({
        ref,
        panelRef,
        fromSectionRef,
        toSectionRef,
        toIndex,
        isPanelAuto,
        isFromSectionPanelsAuto,
        isToSectionPanelsAuto,
        newSectionName,
      })
);

export const undoUpdateSettingsAndSortPanels = createAction(
  '@view/panelBankConfig/undoUpdateSettingsAndSortPanels',
  action =>
    (
      ref: Types.Ref,
      args: {
        workspaceSettingsRef: WorkspaceSettingsTypes.Ref;
        sortAlphabetically: boolean | undefined;
        sectionRefs: PanelBankSectionConfigTypes.Ref[];
        panelRefs: PanelTypes.Ref[][];
        /** Previous panel placement overrides to restore on undo */
        prevOverrides?: Record<string, PanelPlacementOverride>;
      }
    ) =>
      action({
        ref,
        args,
      })
);

/* --- REDUCER HELPERS --- */

// Add a section in PanelBank
export const addPanelBankSectionInternal = (
  state: ViewReducerState,
  ref: Types.Ref,
  sectionRef: PanelBankSectionConfigTypes.Ref, // the existing section that you're inserting before or after
  options: {
    addAfter?: boolean;
    newSectionName?: string;
    panelRefs?: PanelTypes.Ref[];
    callbackFn?: (
      newSectionRef: PanelBankSectionConfigTypes.Ref,
      newPanelRefs: PanelTypes.Ref[]
    ) => void;
  }
) => {
  const {addAfter, newSectionName, panelRefs = [], callbackFn} = options;
  const newSectionRef = Normalize.addObj(
    state.parts,
    'panel-bank-section-config',
    ref.viewID,
    {
      ...getDefaultPanelSectionConfig({name: newSectionName}),
      isOpen: true,
      panels: panelRefs.map(panelRef =>
        Normalize.denormalize(state.parts, panelRef)
      ),
    }
  );

  const sectionIndex = state.parts[ref.type][ref.id].sectionRefs.findIndex(
    sr => sr.id === sectionRef.id
  );
  // If the user adds a section, make sure we set state to ready, so
  // that the newly added section is still there upon reload.
  state.parts[ref.type][ref.id].state = PanelBankConfigState.Ready;
  state.parts[ref.type][ref.id].sectionRefs.splice(
    addAfter ? sectionIndex + 1 : sectionIndex,
    0,
    newSectionRef
  );

  if (callbackFn != null) {
    const newSectionPart = lookupPart(state.parts, newSectionRef);
    callbackFn(newSectionRef, newSectionPart.panelRefs);
  }

  return newSectionRef;
};

export const deletePanelBankSectionInternal = (
  draftState: ViewReducerState,
  ref: Types.Ref,
  sectionRef: PanelBankSectionConfigTypes.Ref, // the section that you're deleting
  workspaceSettingsRef?: WorkspaceSettingsTypes.Ref
) => {
  const originalState = original(
    castDraft(draftState)
  ) as Readonly<ViewReducerState>;
  const originalSection = originalState.parts[sectionRef.type][sectionRef.id];
  const draftSection = draftState.parts[sectionRef.type][sectionRef.id];

  // Delete section panels
  const deletePanelsResult = PanelUtils.deletePanels(
    draftState,
    draftSection.panelRefs,
    sectionRef,
    ref,
    workspaceSettingsRef
  );

  // Remove the sectionRef from panelBankConfig
  const panelBankDraft = draftState.parts[ref.type][ref.id];
  const sectionIndex = panelBankDraft.sectionRefs.findIndex(
    s => s.id === sectionRef.id
  );
  panelBankDraft.sectionRefs.splice(sectionIndex, 1);

  // Delete the section
  removeHistoryForObject(draftState, sectionRef);
  deleteParts(draftState, sectionRef);

  return putSection(
    ref,
    sectionRef,
    originalSection,
    sectionIndex,
    deletePanelsResult,
    workspaceSettingsRef
  );
};

// Move a panel in PanelBank
export const movePanelInternal = (
  state: ViewReducerState,
  args: {
    ref: Types.Ref;
    panelRef: PanelTypes.Ref;
    fromSectionRef: PanelBankSectionConfigTypes.Ref;
    toSectionRef: PanelBankSectionConfigTypes.Ref;
    toIndex: number;
    inactivePanelRefIDs?: Set<string>;
    isPanelAuto: boolean | undefined;
  }
) => {
  const {
    ref,
    fromSectionRef,
    toSectionRef,
    panelRef,
    toIndex,
    inactivePanelRefIDs = new Set(),
    isPanelAuto,
  } = args;

  // Update panel.isAuto flag; track prev value for undo
  const panel = state.parts[panelRef.type][panelRef.id];
  const prevIsPanelAuto = panel.isAuto;
  panel.isAuto = isPanelAuto;

  // Track prev section.isPanelsAuto values for undo
  const fromSection = state.parts[fromSectionRef.type][fromSectionRef.id];
  const toSection = state.parts[toSectionRef.type][toSectionRef.id];
  const prevIsFromSectionPanelsAuto = fromSection.isPanelsAuto;
  const prevIsToSectionPanelsAuto = toSection.isPanelsAuto;

  // set manual sorting to section chart was moved to
  // TODO: improve this by checking if the panel was placed
  // in a position respecting or automatically placing it in
  // the sorted position and showing a tool tip
  state.parts[toSectionRef.type][fromSectionRef.id].sorted =
    SectionPanelSorting.Manual;

  const panelRefs =
    state.parts[fromSectionRef.type][fromSectionRef.id].panelRefs;
  // The panel's index in the fromSection
  const fromIndex = panelRefs.findIndex(pRef => pRef.id === panelRef.id);
  // Remove the panelRef from the old position
  state.parts[fromSectionRef.type][fromSectionRef.id].panelRefs.splice(
    fromIndex,
    1
  );
  // Insert the panelRef in the new position
  // HAX: The toIndex provided does not account for inactive (hidden) panels.
  // We need to splice it to the index as if hidden panels are shown.
  let toIndexOffset = 0;
  let activePanelI = 0;
  for (const pr of state.parts[toSectionRef.type][toSectionRef.id].panelRefs) {
    if (activePanelI >= toIndex) {
      break;
    }
    if (inactivePanelRefIDs.has(pr.id)) {
      toIndexOffset++;
    } else {
      activePanelI++;
    }
  }
  state.parts[toSectionRef.type][toSectionRef.id].panelRefs.splice(
    toIndex + toIndexOffset,
    0,
    panelRef
  );
  // If we're moving the panel to a Grid section, we need to add layout to the panel
  if (state.parts[toSectionRef.type][toSectionRef.id].type === 'grid') {
    state.parts[panelRef.type][panelRef.id].layout = {
      ...findNextPanelLoc(
        Normalize.denormalize(state.parts, toSectionRef)
          .panels.map(p => p.layout)
          .filter(p => p),
        GRID_COLUMN_COUNT,
        GRID_ITEM_DEFAULT_WIDTH
      ),
      w: GRID_ITEM_DEFAULT_WIDTH,
      h: GRID_ITEM_DEFAULT_HEIGHT,
    };
  }

  // Save the toSection as the new defaultMoveToSectionName
  const toSectionName = state.parts[toSectionRef.type][toSectionRef.id].name;
  const prevSettings = state.parts[ref.type][ref.id].settings;
  state.parts[ref.type][ref.id].settings = {
    ...prevSettings,
    defaultMoveToSectionName: toSectionName,
  };

  return {
    fromIndex,
    prevIsPanelAuto,
    prevIsFromSectionPanelsAuto,
    prevIsToSectionPanelsAuto,
  };
};
